import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { addToast } from "./toastSlice.js";
import { api } from "../api/baseApi.js";
import { logoutUser } from "./authSlice.js";

// Thunk: upload sales transactions to /api/sales/upload
export const uploadSalesTransactions = createAsyncThunk(
  "salesImport/uploadSalesTransactions",
  async ({ file, extraFields }, { getState, dispatch, rejectWithValue }) => {
    try {
      if (!file) {
        const message = "No file provided";
        dispatch(
          addToast({
            message,
            source: "sales-upload",
            code: "400",
            type: "error",
          })
        );
        return rejectWithValue(message);
      }

      const token = getState()?.auth?.token;
      const form = new FormData();
      form.append("file", file);
      if (extraFields && typeof extraFields === "object") {
        Object.entries(extraFields).forEach(([key, value]) => {
          form.append(key, String(value));
        });
      }

      const res = await fetch(`/api/sales/upload`, {
        method: "POST",
        body: form,
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const contentType = res.headers.get("content-type") || "";
      const parseBody = async () =>
        contentType.includes("application/json")
          ? await res.json()
          : await res.text();

      if (!res.ok) {
        const err = await parseBody().catch(() => ({}));
        const message =
          typeof err === "string" ? err : err?.message || "Upload failed";
        dispatch(
          addToast({
            message,
            source: "sales-upload",
            code: String(res.status || "ERR"),
            type: "error",
            showViewLog: true,
          })
        );
        return rejectWithValue(err.errors || err);
      }

      const data = await parseBody();

      if (data) {
        dispatch(
          addToast({
            message: "Sales transactions uploaded successfully",
            source: "sales-upload",
            code: "200",
            type: "success",
          })
        );
        dispatch(api.util.invalidateTags([{ type: ["Invoices", "Creditnotes"] }]));
      }

      return data;
    } catch (e) {
      const message = e.message || "Unexpected error";
      dispatch(
        addToast({
          message,
          source: "sales-upload",
          code: "ERR",
          type: "error",
        })
      );
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  status: "idle", // 'idle' | 'pending' | 'succeeded' | 'failed'
  error: null,
  result: null,
};

const salesImportSlice = createSlice({
  name: "salesImport",
  initialState,
  reducers: {
    resetSalesImportState: () => ({ ...initialState }),
    addSalesImportError: (state, action) => {
      state.status = "failed";
        Array.isArray(action.payload)
          ? (state.error = action.payload)
          : (state.error = [action.payload] || action.error?.message || "Upload failed");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logoutUser.fulfilled, () => initialState)
      .addCase(uploadSalesTransactions.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(uploadSalesTransactions.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.result = action.payload;
      })
      .addCase(uploadSalesTransactions.rejected, (state, action) => {
        state.status = "failed";
        Array.isArray(action.payload)
          ? (state.error = action.payload)
          : (state.error = [action.payload] || action.error?.message || "Upload failed");
      });
  },
});

export const { resetSalesImportState, addSalesImportError } = salesImportSlice.actions;

// Selectors
export const selectSalesImportStatus = (state) => state.salesImport.status;
export const selectSalesImportIsPending = (state) =>
  state.salesImport.status === "pending";
export const selectSalesImportIsFulfilled = (state) =>
  state.salesImport.status === "succeeded";
export const selectSalesImportIsRejected = (state) =>
  state.salesImport.status === "failed";
export const selectSalesImportError = (state) => state.salesImport.error;
export const selectSalesImportResult = (state) => state.salesImport.result;

export default salesImportSlice.reducer;
